import { cx } from 'class-variance-authority';
import useEmblaCarousel, {
  type EmblaViewportRefType,
} from 'embla-carousel-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ComponentProps,
} from 'react';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';
import ArrowLeft from '../../assets/icons/arrow-left-solid.svg';
import ArrowRight from '../../assets/icons/arrow-right-solid.svg';

interface CarouselContextProps {
  carouselRef: EmblaViewportRefType;
  slidesToShow: 'auto' | number;
}

const CarouselContext = createContext<CarouselContextProps | null>(null);

function useCarousel() {
  const context = useContext(CarouselContext);
  if (!context) {
    throw new Error('useCarousel must be used within a Carousel');
  }
  return context;
}

export interface CarouselProps extends ComponentProps<'div'> {
  /**
   * Number of slides to show at once
   * @default 'auto'
   */
  slidesToShow?: 'auto' | number;
  /**
   * Number of slides to scroll at once
   * @default 'auto'
   */
  slidesToScroll?: 'auto' | number;
  /**
   * Options of Embla Carousel
   * https://www.embla-carousel.com/api/options/#usage
   */
  options?: Parameters<typeof useEmblaCarousel>[0];
  /**
   * Plugins of Emla Carousel
   * https://www.embla-carousel.com/api/plugins/
   */
  plugins?: Parameters<typeof useEmblaCarousel>[1];
  /**
   * Label for previous slide button
   * @default 'previous slide'
   */
  prevSlideLabel?: string;
  /**
   * Label for next slide button
   * @default 'next slide'
   */
  nextSlideLabel?: string;
  /**
   * Always float navigation buttons even on mobile
   * @default false
   */
  floatingNav?: boolean;
}

export function Carousel({
  className,
  children,
  slidesToShow = 'auto',
  slidesToScroll = 'auto',
  options,
  plugins,
  prevSlideLabel,
  nextSlideLabel,
  floatingNav = false,
  ...props
}: CarouselProps) {
  const [carouselRef, api] = useEmblaCarousel(
    {
      loop: true,
      dragFree: true,
      slidesToScroll,
      ...options,
    },
    [WheelGesturesPlugin(), ...(plugins ?? [])]
  );
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const contextValue = useMemo(
    () => ({ carouselRef, slidesToShow }),
    [carouselRef, slidesToShow]
  );

  const updateScrollStatus = useCallback(() => {
    if (!api) {
      return;
    }
    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());

    if (!api.canScrollPrev() && !api.canScrollNext()) {
      api.plugins().wheelGestures?.destroy();
    }
  }, [api]);

  useEffect(() => {
    if (!api) {
      return undefined;
    }

    updateScrollStatus();
    api.on('reInit', updateScrollStatus);
    api.on('select', updateScrollStatus);

    return () => {
      api?.off('select', updateScrollStatus);
    };
  }, [api, updateScrollStatus]);

  return (
    <CarouselContext.Provider value={contextValue}>
      <div className={cx('relative', className)} {...props}>
        {children}
        <div
          className={cx(
            'flex justify-between tablet:p-0',
            !floatingNav && 'p-4'
          )}
        >
          {canScrollPrev && (
            <button
              type="button"
              className={cx(
                floatingNav &&
                  'absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2',
                'rounded-full border border-neutral-1100 bg-brand-white p-[10px] focus-ring hover:bg-neutral-100 tablet:absolute tablet:left-0 tablet:top-1/2 tablet:-translate-x-1/2 tablet:-translate-y-1/2'
              )}
              aria-label={prevSlideLabel ?? 'previous slide'}
              onClick={() => api?.scrollPrev()}
            >
              <ArrowLeft aria-hidden="true" />
            </button>
          )}
          {canScrollNext && (
            <button
              type="button"
              className={cx(
                floatingNav &&
                  'absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2',
                'rounded-full border border-neutral-1100 bg-brand-white p-[10px] focus-ring hover:bg-neutral-100 tablet:absolute tablet:right-0 tablet:top-1/2 tablet:-translate-y-1/2 tablet:translate-x-1/2'
              )}
              aria-label={nextSlideLabel ?? 'next slide'}
              onClick={() => api?.scrollNext()}
            >
              <ArrowRight aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
    </CarouselContext.Provider>
  );
}

export function CarouselContent({
  className,
  ...props
}: ComponentProps<'div'>) {
  const { carouselRef } = useCarousel();

  return (
    <div ref={carouselRef} className="overflow-hidden">
      <div className={cx('-ml-4 flex tablet:-ml-6', className)} {...props} />
    </div>
  );
}

export function CarouselItem({
  className,
  style,
  ...props
}: ComponentProps<'div'>) {
  const { slidesToShow } = useCarousel();

  return (
    <div
      className={cx(
        'grid min-w-0 shrink-0 grow-0 place-items-center pl-4 tablet:pl-6',
        className
      )}
      style={{
        flexBasis:
          slidesToShow === 'auto' ? 'auto' : `calc(100% / ${slidesToShow})`,
        ...style,
      }}
      {...props}
    />
  );
}
